import { Injectable } from '@angular/core';
import { SyntaxHighlighterService } from './syntax-highlighter.service';
import { TableColumnAlignment } from '../models/table-column-alignment.model';

@Injectable({
  providedIn: 'root'
})
export class MessageFormatterService {
  constructor(private readonly syntaxHighlighter: SyntaxHighlighterService) {}

  /**
   * Formats message content with markdown and code highlighting
   * @param content The raw message content
   * @returns Formatted HTML string
   */
  formatContent(content: string): string {
    if (!content) return '';

    let formatted = content;

    // Step 1: Escape HTML to prevent XSS attacks
    formatted = this.escapeHtml(formatted);

    // Step 2: Convert literal \n strings to actual newlines
    formatted = formatted.replace(/\\n/g, '\n');

    // Step 3: Format code blocks (must be done before inline code)
    formatted = this.formatCodeBlocks(formatted);

    // Step 4: Format inline code
    formatted = this.formatInlineCode(formatted);

    // Step 5: Format text styles (bold, italic, strikethrough)
    formatted = this.formatTextStyles(formatted);

    // Step 6: Format links
    formatted = this.formatLinks(formatted);

    // Step 7: Format headers
    formatted = this.formatHeaders(formatted);

    // Step 8: Format tables (before lists and rules, which would rewrite a "- | -" separator row)
    formatted = this.formatTables(formatted);

    // Step 9: Format lists
    formatted = this.formatLists(formatted);

    // Step 10: Format blockquotes
    formatted = this.formatBlockquotes(formatted);

    // Step 11: Format horizontal rules
    formatted = this.formatHorizontalRules(formatted);

    // Step 12: Format line breaks
    formatted = this.formatLineBreaks(formatted);

    // Step 13: Clean up extra line breaks around block elements
    formatted = this.cleanupLineBreaks(formatted);

    return formatted;
  }

  /**
   * Escapes HTML characters to prevent XSS
   * @param text The text to escape
   * @returns Escaped HTML string
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Formats code blocks with syntax highlighting
   * @param content The content to process
   * @returns Content with formatted code blocks
   */
  private formatCodeBlocks(content: string): string {
    return content.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, language, code) => {
      const lang = language ? ` data-language="${language}"` : '';
      let processedCode = code.trim();

      // Apply SQL syntax highlighting if it's SQL
      if (language && language.toLowerCase() === 'sql') {
        processedCode = this.syntaxHighlighter.highlightSql(processedCode);
      }

      return `<div class="code-block-wrapper${language && language.toLowerCase() === 'sql' ? ' sql-code-block' : ''}">
      ${language ? `<div class="code-language">${language}</div>` : ''}
      <pre class="code-block"${lang}><code>${processedCode}</code></pre>
    </div>`;
    });
  }

  /**
   * Formats inline code (single backticks)
   * @param content The content to process
   * @returns Content with formatted inline code
   */
  private formatInlineCode(content: string): string {
    return content.replace(/`([^`\n]+)`/g, '<code class="inline-code">$1</code>');
  }

  /**
   * Formats text styles: bold, italic, strikethrough
   * @param content The content to process
   * @returns Content with formatted text styles
   */
  private formatTextStyles(content: string): string {
    let formatted = content;

    // Bold (**text** or __text__)
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>');

    // Italic (*text* or _text_)
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>');

    // Strikethrough (~~text~~)
    formatted = formatted.replace(/~~(.*?)~~/g, '<del>$1</del>');

    return formatted;
  }

  /**
   * Formats links [text](url)
   * @param content The content to process
   * @returns Content with formatted links
   */
  private formatLinks(content: string): string {
    let result = '';
    let i = 0;

    while (i < content.length) {
      if (content[i] === '[') {
        const closeBracket = content.indexOf(']', i + 1);
        if (closeBracket !== -1 && content[closeBracket + 1] === '(') {
          const closeParen = content.indexOf(')', closeBracket + 2);
          if (closeParen !== -1) {
            const text = content.substring(i + 1, closeBracket);
            const url = content.substring(closeBracket + 2, closeParen);

            result += `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            i = closeParen + 1;
            continue;
          }
        }
      }

      result += content[i];
      i++;
    }

    return result;
  }

  /**
   * Formats markdown headers (# ## ### etc.)
   * @param content The content to process
   * @returns Content with formatted headers
   */
  private formatHeaders(content: string): string {
    let formatted = content;
    formatted = formatted.replace(/^### (.*$)/gm, '<h3>$1</h3>');
    formatted = formatted.replace(/^## (.*$)/gm, '<h2>$1</h2>');
    formatted = formatted.replace(/^# (.*$)/gm, '<h1>$1</h1>');
    return formatted;
  }

  /**
   * Formats both ordered and unordered lists
   * @param content The content to process
   * @returns Content with formatted lists
   */
  private formatLists(content: string): string {
    const lines = content.split('\n');
    const processedLines: string[] = [];
    let inUnorderedList = false;
    let inOrderedList = false;

    for (const line of lines) {
      const trimmedLine = line.trim();

      const unorderedMatch = trimmedLine.match(/^[-*]\s(.+)$/);
      const orderedMatch = trimmedLine.match(/^(\d+)\.\s(.+)$/);

      if (unorderedMatch) {
        // This is an unordered list item
        if (!inUnorderedList) {
          // Starting a new unordered list
          if (inOrderedList) {
            processedLines.push('</ol>');
            inOrderedList = false;
          }
          processedLines.push('<ul>');
          inUnorderedList = true;
        }
        processedLines.push(`<li>${unorderedMatch[1]}</li>`);
      } else if (orderedMatch) {
        // This is an ordered list item
        if (!inOrderedList) {
          // Starting a new ordered list
          if (inUnorderedList) {
            processedLines.push('</ul>');
            inUnorderedList = false;
          }
          processedLines.push('<ol>');
          inOrderedList = true;
        }
        processedLines.push(`<li>${orderedMatch[2]}</li>`);
      } else {
        // Not a list item
        if (inUnorderedList) {
          processedLines.push('</ul>');
          inUnorderedList = false;
        }
        if (inOrderedList) {
          processedLines.push('</ol>');
          inOrderedList = false;
        }
        processedLines.push(line);
      }
    }

    // Close any open lists at the end
    if (inUnorderedList) {
      processedLines.push('</ul>');
    }
    if (inOrderedList) {
      processedLines.push('</ol>');
    }

    return processedLines.join('\n');
  }

  /**
   * Formats blockquotes (> at start of line)
   * @param content The content to process
   * @returns Content with formatted blockquotes
   */
  private formatBlockquotes(content: string): string {
    return content.replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>');
  }

  /**
   * Formats horizontal rules (--- or ***)
   * @param content The content to process
   * @returns Content with formatted horizontal rules
   */
  private formatHorizontalRules(content: string): string {
    return content.replace(/^(---|\*\*\*)$/gm, '<hr>');
  }

  /**
   * Formats GitHub-flavored markdown pipe tables, leaving code blocks untouched
   * @param content The content to process
   * @returns Content with formatted tables
   */
  private formatTables(content: string): string {
    return content
      .split(/(<pre[\s\S]*?<\/pre>)/)
      .map(segment => (segment.startsWith('<pre') ? segment : this.formatTablesInText(segment)))
      .join('');
  }

  private formatTablesInText(content: string): string {
    const lines = content.split('\n');
    const processedLines: string[] = [];
    let index = 0;

    while (index < lines.length) {
      if (this.isTableStart(lines, index)) {
        const header = this.splitTableRow(lines[index]);
        const alignments = this.splitTableRow(lines[index + 1]).map(cell => this.readColumnAlignment(cell));
        const bodyRows: string[][] = [];
        index += 2;
        while (index < lines.length && this.isTableRow(lines[index])) {
          bodyRows.push(this.splitTableRow(lines[index]));
          index++;
        }
        processedLines.push(this.buildTableHtml(header, alignments, bodyRows));
      } else {
        processedLines.push(lines[index]);
        index++;
      }
    }

    return processedLines.join('\n');
  }

  private isTableStart(lines: string[], index: number): boolean {
    return (
      index + 1 < lines.length &&
      this.isTableRow(lines[index]) &&
      this.isTableSeparator(lines[index + 1]) &&
      this.splitTableRow(lines[index]).length === this.splitTableRow(lines[index + 1]).length
    );
  }

  private isTableRow(line: string): boolean {
    return line.trim() !== '' && line.includes('|') && !/^\s*<h[1-6]\b/.test(line) && !this.isListItem(line);
  }

  private isListItem(line: string): boolean {
    return /^\s*([-*]|\d+\.)\s/.test(line);
  }

  private isTableSeparator(line: string): boolean {
    return /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(line);
  }

  private splitTableRow(line: string): string[] {
    let row = line.trim();
    if (row.startsWith('|')) {
      row = row.substring(1);
    }
    if (row.endsWith('|') && !row.endsWith('\\|')) {
      row = row.substring(0, row.length - 1);
    }
    return row.split(/(?<!\\)\|/).map(cell => cell.replace(/\\\|/g, '|').trim());
  }

  private readColumnAlignment(separatorCell: string): TableColumnAlignment {
    const alignsLeft = separatorCell.startsWith(':');
    const alignsRight = separatorCell.endsWith(':');
    if (alignsLeft && alignsRight) {
      return 'center';
    }
    if (alignsRight) {
      return 'right';
    }
    return alignsLeft ? 'left' : null;
  }

  // Emitted on one line so formatLineBreaks cannot put <br> inside the table.
  // Alignment is a class, not a style attribute, because Angular's [innerHTML] sanitizer strips style.
  // tabindex lets keyboard users scroll a wide table; some browsers (Safari) do not focus scroll areas by themselves.
  private buildTableHtml(header: string[], alignments: TableColumnAlignment[], bodyRows: string[][]): string {
    const columnCount = header.length;
    const buildCell = (tag: 'th' | 'td', cell: string, column: number): string => {
      const alignment = alignments[column];
      const alignmentClass = alignment ? ` class="md-table__cell--${alignment}"` : '';
      return `<${tag}${alignmentClass}>${cell}</${tag}>`;
    };
    const headHtml = header.map((cell, column) => buildCell('th', cell, column)).join('');
    const bodyHtml = bodyRows
      .map(row => {
        const cells = Array.from({ length: columnCount }, (_, column) => buildCell('td', row[column] ?? '', column));
        return `<tr>${cells.join('')}</tr>`;
      })
      .join('');

    return `<div class="md-table-wrapper app-scrollbar" role="region" aria-label="Table" tabindex="0"><table class="md-table"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`;
  }

  /**
   * Converts newlines to <br> tags, preserving HTML structure
   * @param content The content to process
   * @returns Content with formatted line breaks
   */
  private formatLineBreaks(content: string): string {
    return content.replace(/\n(?!<\/?(h[1-6]|li|ul|ol|blockquote|pre|div))/g, '<br>');
  }

  /**
   * Cleans up extra line breaks around block elements
   * @param content The content to process
   * @returns Cleaned content
   */
  private cleanupLineBreaks(content: string): string {
    let cleaned = content;
    cleaned = cleaned.replace(/<br>\s*(<\/?(h[1-6]|li|ul|ol|blockquote|pre|div|hr)[^>]*>)\s*<br>/g, '$1');
    cleaned = cleaned.replace(/(<\/?(h[1-6]|li|ul|ol|blockquote|pre|div|hr)[^>]*>)\s*<br>/g, '$1');
    cleaned = cleaned.replace(/<br>\s*(<\/?(h[1-6]|li|ul|ol|blockquote|pre|div|hr)[^>]*>)/g, '$1');
    return cleaned;
  }
}
